// --- React & Redux
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Typography from '@dtci/spellbook/Typography';
// --- Components
import { PaginationBar } from 'datastage/components/PaginationBar';
import ToastNotificationProvider from 'datastage/components/ToastNotifications';
import { DealVersionGrid } from './components/grid';
import { DealVersionSearchForm } from './components/search-form';
import { DealStatusDrawer } from './components/deal-status-drawer';
import { TwoRowsLayout } from './layout/two-row';
// --- Hooks & Utils
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { usePermissions } from 'datastage/utils/permissions';
import { getItemRange, getAllItemsRange } from 'datastage/utils/pagination';
import { exportToCsv } from 'datastage/utils/export/exportToCsv';
import { flattenDealVersions } from 'datastage/utils/export/flattenedDealVersionData';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { useStoredItemsPerPageSync } from 'datastage/utils/hooks/useStoredItemsPerPageSync';
import { PAGINATION_STORAGE_KEYS } from 'datastage/utils/paginationPreferences';
// --- API & Store
import { useDealVersionData } from './hooks/useDealVersionData';

/**
 * Deal Version Page
 *
 * Features:
 * - Displays a paginated list of deal versions
 * - Provides search, export, and refresh functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 * - Implements refreshDealVersion flag pattern for clean separation of concerns
 * - Persists page size preference to localStorage automatically
 *
 * @component
 * @returns {JSX.Element} The rendered deal version management page.
 */
export const DealVersionPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showErrorToast } = useToasts();
    const { isDealVersioningReadOnly } = usePermissions();
    const { fetchDealVersions, updateDealVersion, rangeParam, itemsPerPage } = useDealVersionData();
    const { totalPages, currentPage, totalItems, refreshDealVersion, pageSizeOptions } = useSelector(
        (state) => state.dealVersion,
    );

    /**
     * Sync persisted page size preference from localStorage on mount.
     * If stored preference differs from Redux state, automatically updates Redux.
     */
    useStoredItemsPerPageSync({
        storageKey: PAGINATION_STORAGE_KEYS.dealVersion,
        itemsPerPage,
        rangeKey: 'rangeParam',
        onSync: updateDealVersion,
    });

    /**
     * Listen for refreshDealVersion flag and refresh the grid.
     * This pattern allows other components (like drawers) to trigger refreshes
     * without duplicating fetch logic.
     */
    useEffect(() => {
        if (refreshDealVersion) {
            handleRefresh();
            updateDealVersion({ refreshDealVersion: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshDealVersion]);

    /**
     * Handle page change for pagination (←/→ buttons).
     * Fetches directly without updating URL (TAP-UI pattern).
     * Page size changes go through URL → search form handles those.
     */
    const handlePageChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;

        const nextPageRange = getItemRange(pageNumber, itemsPerPage);
        const data = await fetchDealVersions(nextPageRange);

        if (data) {
            updateDealVersion({
                dealVersions: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                totalItems: data?.totalElements ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                rangeParam: nextPageRange,
            });
        }
    };

    /**
     * Handle page size change from dropdown.
     * Updates URL with new page size - the search form will handle the fetch.
     * This prevents duplicate fetches (TAP-UI pattern).
     */
    const handlePageSizeChange = (newSize) => {
        if (newSize === itemsPerPage) return;

        // Update URL with new page size
        // Search form will see URL change and fetch with new size
        const currentParams = parseSearchParams(location.search);
        updateSearchParams(location, navigate, {
            ...currentParams,
            pageSize: newSize,
        });
    };

    /**
     * Refresh the deal version list from the API.
     */
    const handleRefresh = async () => {
        const data = await fetchDealVersions(rangeParam);

        if (data) {
            updateDealVersion({
                dealVersions: data?.content ?? [],
                totalItems: data?.totalElements ?? 0,
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
            });
        }
    };

    /**
     * Open the deal status update drawer.
     */
    const handleUpdateDealStatus = () => {
        updateDealVersion({ isDealStatusOpen: true });
    };

    /**
     * Export all deal versions to CSV.
     * Fetches all records using the total count from the current result set.
     */
    const handleExport = async () => {
        const exportRange = getAllItemsRange(totalItems);
        const data = await fetchDealVersions(exportRange);

        if (data?.code) {
            showErrorToast(data.message || 'An unexpected error occurred');
            return;
        }

        const filteredContent = flattenDealVersions(data?.content || []);
        exportToCsv(filteredContent, 'deal_version_list');
    };

    return (
        <>
            <Row
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 20,
                }}
            >
                <Col>
                    <Typography variant="T50" style={{ marginLeft: 20 }}>
                        Deal Version
                    </Typography>
                </Col>
                <Col style={{ display: 'flex', gap: 8 }}>
                    {!isDealVersioningReadOnly && (
                        <Button
                            iconType="fa-solid fa-pencil"
                            labelText="Update Deal Status"
                            styleType="link"
                            onClick={handleUpdateDealStatus}
                        >
                            Update Deal Status
                        </Button>
                    )}
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                        disabled={totalItems === 0}
                    >
                        Export
                    </Button>
                    <Button
                        iconType="fa-solid fa-rotate"
                        labelText="Refresh"
                        styleType="link"
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>

            <TwoRowsLayout>
                <main data-position="top">
                    <DealVersionSearchForm />
                </main>
                <main data-position="bottom">
                    <DealVersionGrid />
                </main>
            </TwoRowsLayout>

            <PaginationBar
                totalPages={totalPages}
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={pageSizeOptions}
            />

            {/* Deal status drawer for updating deal status */}
            <DealStatusDrawer />

            <ToastNotificationProvider />
        </>
    );
};
